import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { IconSearch, Select, TextField, useDebounce, Text } from 'uikit';

import type React from 'react';
import { useCallback, useState } from 'react';

import { usePriceListUerForConstraintRatioColumns } from './hooks/usePriceListUerForConstraintRatioColumns';
import { usePriceListUerForConstraintRatioData } from './hooks/usePriceListUerForConstraintRatioData';
import type { ICheckedChangeItem } from './hooks/usePriceListUerForTechPartRatioColumns';

import Styles from './ApplicationAreaSelector.module.css';

interface IPriceListUerForConstraintRatioTable {
    onRowSelectChange: (changeState: ICheckedChangeItem[]) => void;
    selectedId: number;
}

export const PriceListUerForConstraintRatioTable: React.FC<IPriceListUerForConstraintRatioTable> = (props) => {
    const { selectedId, onRowSelectChange } = props;

    const [searchValue, setSearchValue] = useState<string>('');
    const [debounceSearchValue, setDebounceSearchValue] = useState<string>('');
    const [currentFilters, setCurrentFilters] = useState<Record<string, string[]> | null>(null);

    const onFiltersUpdated = useCallback(
        ({ fieldName, checkedValues }: { fieldName: string; checkedValues: string[] }) => {
            setCurrentFilters((prev) => ({
                ...prev,
                [fieldName]: checkedValues,
            }));
        },
        [],
    );

    const onSearchDebouncedSearchValueChange = useDebounce((v) => {
        setDebounceSearchValue(v);
    }, 700);

    const onSearchValueChange = useCallback(
        ({ value }) => {
            setSearchValue(value);
            onSearchDebouncedSearchValueChange(value);
        },
        [onSearchDebouncedSearchValueChange],
    );

    const { selectItems, selectedGroup, handleChangeSelectedGroup, selectItemsLoading, dataSource } =
        usePriceListUerForConstraintRatioData({
            searchValue: debounceSearchValue,
            currentFilters,
        });

    const columns = usePriceListUerForConstraintRatioColumns({
        onRowSelectChange,
        selectedId,
        onFiltersUpdated,
    });

    return (
        <>
            <div className={Styles.selectSearch}>
                <Select
                    placeholder="Вид работ"
                    items={selectItems}
                    value={selectedGroup}
                    getItemLabel={(item) => item.name}
                    onChange={handleChangeSelectedGroup}
                    className={Styles.groupSelect}
                    isLoading={selectItemsLoading}
                />
                <TextField
                    className={Styles.search}
                    placeholder="Поиск расценки в прейскуранте"
                    leftSide={IconSearch}
                    value={searchValue}
                    onChange={onSearchValueChange}
                    withClearButton
                    disabled={!selectedGroup}
                />
            </div>

            {selectedGroup ? (
                <AgGridTable
                    key={`${selectedGroup?.id}-${debounceSearchValue}`}
                    columnDefs={columns}
                    datasource={dataSource}
                    rowModelType="infinite"
                    maxConcurrentDatasourceRequests={1}
                    maxBlocksInCache={50}
                    cacheBlockSize={50}
                    rowClassRules={{
                        groupRow: ({ data }) => (data as any)?.type === 'section',
                    }}
                    getRowId={({ data }) => (data as any)?.groupRowId ?? data?.id}
                />
            ) : (
                <Text size="s" view="secondary" className={Styles.selectGroupText}>
                    Для отображения данных выберите вид работ
                </Text>
            )}
        </>
    );
};

PriceListUerForConstraintRatioTable.displayName = 'PriceListUerForConstraintRatioTable';
